import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SiteNav from './SiteNav';
import BlogFooter from './BlogFooter';
import SEO from './SEO';

/* ── Flip Card ── */
interface FlipCardProps {
  num: string;
  title: string;
  desc: string;
  backTitle: string;
  backBody: string;
  backRef: string;
}

function FlipCard({ num, title, desc, backTitle, backBody, backRef }: FlipCardProps) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className="h-[220px] cursor-pointer group"
      onClick={() => setFlipped(f => !f)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFlipped(f => !f); } }}
      tabIndex={0}
      role="button"
      aria-label={title}
      style={{ perspective: '1000px' }}
    >
      <div
        className="relative w-full h-full transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 bg-sand/10 p-7 flex flex-col justify-between"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div>
            <div className="text-[10px] tracking-[3px] uppercase text-amber font-medium mb-3">{num}</div>
            <div className="font-serif text-[17px] font-bold text-ink leading-snug">{title}</div>
          </div>
          <div>
            <p className="text-[13px] text-taupe/80 leading-snug">{desc}</p>
            <p className="text-[10px] tracking-[1.5px] uppercase text-taupe/50 mt-2">↺ Retourner</p>
          </div>
        </div>
        {/* Back */}
        <div
          className="absolute inset-0 bg-ink p-7 flex flex-col justify-between"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div>
            <div className="font-serif text-[14px] font-bold text-amber mb-2">{backTitle}</div>
            <p className="text-[13px] text-paper/70 leading-relaxed">{backBody}</p>
          </div>
          <p className="text-[10px] text-amber/50 tracking-wide mt-2">{backRef}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Situation Item ── */
interface SituationProps {
  num: string;
  title: string;
  body: React.ReactNode;
  apport: string;
}

function SituationItem({ num, title, body, apport }: SituationProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="grid grid-cols-1 md:grid-cols-2 border-b border-ink/10 last:border-b-0 bg-paper hover:bg-sand/5 transition-colors duration-200"
    >
      <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-ink/10 relative">
        <div
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-amber transition-transform duration-500 origin-top"
          style={{ transform: visible ? 'scaleY(1)' : 'scaleY(0)' }}
        />
        <div className="text-[10px] tracking-[3px] uppercase text-amber font-medium mb-3">{num}</div>
        <h3 className="font-serif text-[20px] font-bold text-ink leading-tight mb-3">{title}</h3>
        <div className="font-serif text-[18px] text-ink/80 leading-relaxed">{body}</div>
      </div>
      <div className="p-8 md:p-10 flex flex-col justify-center">
        <div className="text-[9px] tracking-[3px] uppercase text-taupe/60 mb-3 font-medium">Ce qu&apos;Amargi apporte</div>
        <p className="text-[14px] text-taupe/80 leading-relaxed" dangerouslySetInnerHTML={{ __html: apport }} />
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function UseCasesPage() {
  const [mode, setMode] = useState<'vivre' | 'accompagner'>('vivre');

  // Manifeste word-by-word reveal
  const manifesteRef = useRef<HTMLDivElement>(null);
  const [manifesteVisible, setManifestVisible] = useState(false);
  useEffect(() => {
    const el = manifesteRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setManifestVisible(true); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const manifesteWords = "Amargi ne guérit pas. Il recrée les conditions dans lesquelles vous pouvez vous guérir vous-même.".split(' ');

  const flipCards: FlipCardProps[] = [
    {
      num: '01', title: "Sensation d'être en apnée",
      desc: 'Une respiration superficielle, comme si vous oubliez de respirer.',
      backTitle: 'Ce qui se passe',
      backBody: "Le système sympathique comprime le diaphragme en état d'alerte. Ce n'est pas un tic nerveux — c'est une réponse de survie qui ne s'est jamais éteinte. La respiration s'est adaptée à un danger permanent.",
      backRef: 'Zaccaro et al., 2018 · Porges, 2001',
    },
    {
      num: '02', title: 'Sommeil haché',
      desc: 'Réveils nocturnes, fatigue résiduelle au matin. Les vacances ne suffisent plus à relâcher la pression.',
      backTitle: 'Ce qui se passe',
      backBody: "Le cortisol nocturne reste élevé quand le système nerveux n'a pas pu désactiver l'état d'alerte en journée. Le corps reste en veille même pendant le sommeil — cherchant des menaces qui ne sont plus là.",
      backRef: 'McEwen, 1998 · Sapolsky, 1994',
    },
    {
      num: '03', title: 'Perte de patience',
      desc: 'Une réactivité émotionnelle accrue face aux imprévus. Vous ne vous reconnaissez plus.',
      backTitle: 'Ce qui se passe',
      backBody: "Le cortex préfrontal — siège de la nuance — se déconnecte quand le système limbique est en surcharge. Ce n'est pas vous. C'est votre neurobiologie en mode survie.",
      backRef: 'Damasio, 1994 · Siegel, 1999',
    },
    {
      num: '04', title: 'Charge mentale saturée',
      desc: "L'impression que votre cerveau ne s'arrête jamais. Même la nuit.",
      backTitle: 'Ce qui se passe',
      backBody: "Un système nerveux dysrégulé ne peut pas passer en mode récupération. Il tourne en boucle — cherchant des menaces même en l'absence de danger réel. La pensée compulsive est une réponse de survie, pas un défaut.",
      backRef: 'Felitti, 1998 · Barrett, 2017',
    },
    {
      num: '05', title: "Sentiment d'être dépassée",
      desc: 'Beaucoup de responsabilités, plusieurs rôles, peu d\'espace pour vous — et la culpabilité de ne pas y arriver.',
      backTitle: 'Ce qui se passe',
      backBody: "Ce n'est pas un problème d'organisation. C'est un problème de charge allostasique — le corps accumule sans pouvoir se vider. La capacité de récupération s'est épuisée avant que vous vous en rendiez compte.",
      backRef: 'Felitti, 1998 · McEwen, 1998',
    },
    {
      num: '06', title: 'Corps qui ne répond plus',
      desc: 'Douleurs diffuses, fatigue inexpliquée, fibromyalgie. Les bilans reviennent normaux.',
      backTitle: 'Ce qui se passe',
      backBody: "Le système nerveux autonome régule la douleur, l'inflammation et la sensibilité. Quand il est désorganisé, le corps parle là où les mots ne vont plus. Ce n'est pas imaginaire — c'est neurobiologique.",
      backRef: 'Tracey, 2002 · Pert, 1997',
    },
  ];

  const situations: SituationProps[] = [
    {
      num: 'Situation 01', title: 'Le deuxième burn-out',
      body: <><span>Le premier, vous l&apos;avez traversé. Vous avez changé de poste, ralenti, peut-être arrêté. Et puis ça a recommencé — différemment, mais avec </span><em className="italic text-taupe/70">la même sensation au fond.</em><span> Parce que ce qui n&apos;a pas été régulé dans le corps revient.</span></>,
      apport: "<strong>Identifier le pattern autonome qui se répète.</strong> Pas traiter les symptômes — modifier ce qui les produit. Comprendre pourquoi le corps bascule, et à quel moment, pour changer la trajectoire avant le prochain cycle.",
    },
    {
      num: 'Situation 02', title: 'Le post-trauma',
      body: <><span>Quelque chose s&apos;est passé. Vous avez fait le travail — thérapie, temps, compréhension. Et pourtant le corps, lui, </span><em className="italic text-taupe/70">n&apos;a pas tout à fait suivi.</em><span> Il sursaute encore. Il se contracte. Il ne fait pas confiance.</span></>,
      apport: "<strong>Compléter ce que la parole ne peut pas atteindre seule.</strong> Le corps a besoin de compléter ce que l'événement a interrompu — en petites doses, en sécurité, au rythme de ce qui est possible maintenant.",
    },
    {
      num: 'Situation 03', title: 'La fibromyalgie et les douleurs inexpliquées',
      body: <><span>Les bilans reviennent normaux. On vous dit que </span><em className="italic text-taupe/70">c&apos;est dans la tête.</em><span> Vous savez que non — mais vous ne savez plus quoi faire de ce que vous ressentez. La douleur est réelle. Elle cherche juste une autre lecture.</span></>,
      apport: "<strong>Une lecture du système nerveux autonome comme infrastructure de la douleur.</strong> Pas guérir — recalibrer le signal. Le SNA régule l'inflammation et la sensibilité. Quand il se réorganise, la douleur peut changer de nature.",
    },
    {
      num: 'Situation 04', title: 'La femme multi-rôles',
      body: <><span>Mère, professionnelle, fille, amie, partenaire. Vous gérez tout — souvent bien. Mais il n&apos;y a plus d&apos;espace pour vous dans votre propre agenda. </span><em className="italic text-taupe/70">Et la fatigue n&apos;est plus seulement physique.</em></>,
      apport: "<strong>90 secondes pour évaluer. Un protocole adapté à ce qui est là maintenant</strong> — pas à ce que vous devriez ressentir. Un outil qui s'intègre dans la vie réelle, pas dans un idéal de bien-être inaccessible.",
    },
    {
      num: 'Situation 05', title: "L'equicoaching et le travail corporel déjà entamé",
      body: <><span>Vous avez déjà travaillé avec le corps — à cheval, en mouvement, en contact. Vous savez que </span><em className="italic text-taupe/70">quelque chose se passe là.</em><span> Amargi donne un cadre neurobiologique à ce que vous avez déjà vécu.</span></>,
      apport: "<strong>Un outil de lecture et de suivi entre les séances.</strong> Ce qui se passe dans le corps pendant une session peut être nommé, mesuré, consolidé. La régulation devient intentionnelle — pas seulement ressentie.",
    },
  ];

  return (
    <>
      <SEO
        title="Cas d'usage | Amargi"
        description="Fatigue chronique, burn-out, stress décisionnel, douleurs diffuses : découvrez comment Amargi aide à réguler le système nerveux selon votre situation."
        path="/cas-usage"
      />
      <div className="min-h-screen bg-paper font-sans">
      <SiteNav />

      {/* ── HÉRO ── */}
      <section className="min-h-screen flex flex-col items-center justify-center bg-ink relative overflow-hidden px-6 pt-[120px] pb-20 text-center">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(196,137,42,0.07) 0%, transparent 65%)' }} />
        <div className="relative z-10 max-w-[700px]">
          <span className="text-[10px] tracking-[4px] uppercase text-paper/30 block mb-7">Cas d&apos;usage</span>
          <h1 className="font-serif text-[clamp(28px,4.5vw,52px)] font-bold leading-[1.15] text-paper mb-7 tracking-tight">
            Votre corps porte<br />
            <em className="not-italic text-amber">plus que les mots</em><br />
            ne peuvent dire.
          </h1>
          <div className="w-9 h-px bg-amber/40 mx-auto mb-6" />
          <p className="font-serif text-[20px] italic text-paper/50 leading-[1.72] max-w-[520px] mx-auto mb-12">
            Retrouvez votre situation. Découvrez ce qui peut changer.
          </p>

          {/* Mode switcher */}
          <div className="inline-flex border border-paper/15 overflow-hidden rounded-sm">
            <button
              onClick={() => setMode('vivre')}
              className={`text-[13px] px-7 py-3 transition-all duration-200 tracking-wide ${mode === 'vivre' ? 'bg-amber text-paper font-medium' : 'bg-transparent text-paper/45 hover:text-paper/75 hover:bg-paper/5'}`}
            >
              Je vis ça
            </button>
            <div className="w-px bg-paper/15 self-stretch" />
            <button
              onClick={() => setMode('accompagner')}
              className={`text-[13px] px-7 py-3 transition-all duration-200 tracking-wide ${mode === 'accompagner' ? 'bg-amber text-paper font-medium' : 'bg-transparent text-paper/45 hover:text-paper/75 hover:bg-paper/5'}`}
            >
              J&apos;accompagne quelqu&apos;un
            </button>
          </div>
        </div>
      </section>

      {/* ── RECONNAISSANCE (mode vivre) ── */}
      <section
        className={`py-24 bg-paper transition-opacity duration-400 ${mode === 'accompagner' ? 'opacity-20 pointer-events-none select-none' : ''}`}
      >
        <div className="max-w-[760px] mx-auto px-6 md:px-12">
          <span className="text-[10px] tracking-[4px] uppercase text-amber block mb-4">Reconnaissance</span>
          <h2 className="font-serif text-[clamp(22px,3vw,36px)] font-bold text-ink leading-tight mb-4 tracking-tight">
            Ce que vous ressentez a un nom.
          </h2>
          <p className="font-serif text-[20px] italic text-ink/70 leading-[1.8] mb-3">
            Ces signaux ne sont pas dans votre tête. Ils sont dans votre système nerveux.
          </p>
          <p className="text-[13px] text-taupe/60 italic mb-14 pl-4 border-l-2 border-taupe/30">
            Cliquez sur une carte pour comprendre ce qui se passe neurobiologiquement.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {flipCards.map(card => (
              <FlipCard key={card.num} {...card} />
            ))}
          </div>
        </div>
      </section>

      {/* ── SITUATIONS DE VIE (mode vivre) ── */}
      <section
        className={`py-24 bg-sand/8 transition-opacity duration-400 ${mode === 'accompagner' ? 'opacity-20 pointer-events-none select-none' : ''}`}
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <div className="max-w-[840px] mb-14">
            <span className="text-[10px] tracking-[4px] uppercase text-amber block mb-4">Les situations</span>
            <h2 className="font-serif text-[clamp(22px,3vw,36px)] font-bold text-ink leading-tight mb-5 tracking-tight">
              Amargi n&apos;est pas réservé<br />aux crises.
            </h2>
            <p className="font-serif text-[20px] text-ink/80 leading-[1.8]">
              Il est utile partout où le corps porte <strong className="font-semibold">plus que les mots ne peuvent dire.</strong> Retrouvez votre situation.
            </p>
          </div>

          <div className="flex flex-col border border-ink/10">
            {situations.map(s => (
              <SituationItem key={s.num} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* ── PRESCRIPTEURS (mode accompagner) ── */}
      <section
        className={`py-24 bg-paper border-t border-ink/8 transition-opacity duration-400 ${mode === 'vivre' ? 'opacity-20 pointer-events-none select-none' : ''}`}
      >
        <div className="max-w-[760px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start">

            <div>
              <span className="text-[10px] tracking-[4px] uppercase text-amber block mb-4">Pour les prescripteurs</span>
              <h2 className="font-serif text-[clamp(22px,3vw,32px)] font-bold text-ink leading-tight mb-6 tracking-tight">
                Vous accompagnez quelqu&apos;un qui a fait le travail — et dont le corps n&apos;a pas encore suivi.
              </h2>
              <p className="font-serif text-[19px] text-ink/80 leading-[1.82] mb-4">
                Amargi n&apos;est pas une alternative à la thérapie. C&apos;est ce qui peut venir après — ou à côté — quand la parole a fait son travail et que le système nerveux autonome n&apos;a pas encore intégré ce que l&apos;esprit a compris.
              </p>
              <p className="font-serif text-[19px] text-ink/80 leading-[1.82] mb-4">
                <strong className="font-semibold">Et inversement :</strong> parfois il est utile de traiter là où les mots <em className="italic text-taupe/70">ne peuvent pas encore aller.</em> Le corps peut ouvrir ce que la parole n&apos;avait pas encore trouvé.
              </p>
              <p className="font-serif text-[19px] text-ink/80 leading-[1.82] mb-8">
                En fin de cycle thérapeutique, certains patients arrivent à la compréhension sans trouver le relâchement. La régulation somatique peut offrir un espace de consolidation — ancré en neurobiologie, mesurable, progressif.
              </p>
              <a
                href="mailto:contact@amargi.me"
                className="inline-flex items-center gap-2 text-[13px] font-medium text-amber border border-amber/30 px-5 py-2.5 rounded-sm hover:bg-amber/5 transition-colors"
              >
                Contacter Bertrand directement →
              </a>
            </div>

            <div>
              <div className="bg-sand/10 border border-ink/8 p-6 mb-4 rounded-sm">
                <div className="text-[9px] tracking-[3px] uppercase text-taupe/60 mb-2">Approche clinique</div>
                <h3 className="font-serif text-[18px] font-bold text-ink mb-3">Complémentaire, non concurrente</h3>
                <p className="text-[13px] text-taupe/80 leading-relaxed mb-4">
                  Amargi travaille sur le registre somatique et autonome — là où la thérapie verbale ne peut pas toujours atteindre directement. Les deux approches se renforcent quand elles coexistent.
                </p>
                <p className="text-[11px] text-amber/60 tracking-wide">Porges, 2001 · van der Kolk, 2014 · Levine, 1997 · Brom et al., 2017</p>
              </div>

              <div className="flex flex-col gap-3">
                {[
                  "Outil de suivi entre séances pour les patients en travail corporel — nommé, mesuré, progressif",
                  "Lecture autonome de l'état du patient — 27 profils, évaluation en 90 secondes",
                  "Ancré en recherche publiée — 20 textes fondateurs, cités dans l'outil",
                  "Approche holistique et complémentaire — pas le même monde, le même objectif",
                ].map((point, i) => (
                  <div key={i} className="flex gap-3 items-start bg-sand/8 px-4 py-3 rounded-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-taupe/50 flex-shrink-0 mt-1.5" />
                    <p className="text-[13.5px] text-ink/80 leading-relaxed" dangerouslySetInnerHTML={{ __html: point.replace(/—/g, '<em class="not-italic text-taupe/60"> — </em>') }} />
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── PHRASE MANIFESTE ── */}
      <section className="py-24 bg-ink">
        <div className="max-w-[700px] mx-auto px-6 text-center" ref={manifesteRef}>
          <p className="font-serif text-[clamp(22px,3.5vw,36px)] leading-[1.5] text-paper/80">
            {manifesteWords.map((word, i) => (
              <span
                key={i}
                className="inline-block transition-all duration-500"
                style={{
                  opacity: manifesteVisible ? 1 : 0,
                  transform: manifesteVisible ? 'translateY(0)' : 'translateY(8px)',
                  transitionDelay: `${i * 0.06}s`,
                }}
              >
                {word === 'vous-même.' ? <em className="not-italic text-amber">{word}</em> : word}
                {i < manifesteWords.length - 1 ? '\u00a0' : ''}
              </span>
            ))}
          </p>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="grid grid-cols-1 md:grid-cols-2">
        {/* Card A */}
        <div className="bg-paper px-10 py-16 flex flex-col justify-between border-r border-ink/8">
          <div>
            <div className="text-[9px] tracking-[3px] uppercase text-taupe/50 mb-4">Je vis ça</div>
            <h3 className="font-serif text-[26px] font-bold text-ink leading-tight mb-4">
              Commencez par vous lire.<br />90 secondes.
            </h3>
            <p className="text-[14px] text-taupe/70 leading-relaxed mb-8">
              Votre état du jour est le point de départ. Pas un protocole universel — une lecture de ce qui est là maintenant.
            </p>
          </div>
          <Link
            to="/fr/evaluation"
            className="inline-flex items-center gap-2 bg-lapis hover:bg-lapis-deep text-paper text-[13.5px] font-semibold px-6 py-3 rounded-full transition-all hover:-translate-y-px hover:shadow-md self-start"
          >
            Accéder à l&apos;outil de régulation →
          </Link>
        </div>

        {/* Card B */}
        <div className="bg-ink px-10 py-16 flex flex-col justify-between">
          <div>
            <div className="text-[9px] tracking-[3px] uppercase text-paper/30 mb-4">J&apos;accompagne quelqu&apos;un</div>
            <h3 className="font-serif text-[26px] font-bold text-paper leading-tight mb-4">
              Parlons de ce que vous observez chez vos patients.
            </h3>
            <p className="text-[14px] text-paper/60 leading-relaxed mb-8">
              Une conversation directe avec Bertrand — pour comprendre comment Amargi peut s&apos;intégrer à votre pratique.
            </p>
          </div>
          <a
            href="mailto:contact@amargi.me"
            className="inline-flex items-center gap-2 border border-paper/30 text-paper text-[13.5px] font-medium px-6 py-3 rounded-full hover:bg-paper/10 transition-all self-start"
          >
            Contacter Bertrand →
          </a>
        </div>
      </section>

      <BlogFooter />
    </div>
    </>
  );
}
